import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export default function TicketCategorySettings() {
  const { data: categories = [], isLoading, refetch } = trpc.tickets.ticketCategories.getAll.useQuery();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: "", nameAr: "" });
  const [isCreating, setIsCreating] = useState(false);

  const createMutation = trpc.tickets.ticketCategories.create.useMutation({
    onSuccess: () => {
      toast.success("Category created");
      refetch();
      setFormData({ name: "", nameAr: "" });
      setIsCreating(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.tickets.ticketCategories.update.useMutation({
    onSuccess: () => {
      toast.success("Category updated");
      refetch();
      setEditingId(null);
      setFormData({ name: "", nameAr: "" });
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.tickets.ticketCategories.delete.useMutation({
    onSuccess: () => {
      toast.success("Category deleted");
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        name: formData.name,
        nameAr: formData.nameAr || undefined,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        nameAr: formData.nameAr || undefined,
      });
    }
  };

  const startEdit = (category: any) => {
    setEditingId(category.id);
    setFormData({ name: category.name, nameAr: category.nameAr || "" });
    setIsCreating(false);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({ name: "", nameAr: "" });
  };

  const cancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ name: "", nameAr: "" });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#6B6B6B]">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="py-6 px-0">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[15px] font-semibold text-[#2C2C2C]">
          {categories.length} categor{categories.length === 1 ? "y" : "ies"}
        </h3>
        {!isCreating && editingId === null && (
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white text-[13px] font-semibold hover:bg-[#6D28D9] transition"
          >
            <Plus className="h-4 w-4" />
            Add Category
          </button>
        )}
      </div>

      {/* Form for creating/editing */}
      {(isCreating || editingId) && (
        <div className="bg-[#F9F8FF] border border-[#E8E0FF] rounded-lg p-4 mb-4">
          <h4 className="text-[13px] font-semibold text-[#2C2C2C] mb-3">
            {editingId ? "Edit Category" : "New Category"}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-[11.5px] font-semibold text-[#6B6B6B] mb-1">
                Category Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Access Requests"
                className="w-full px-3 py-2 rounded-lg border border-[#E0E0E0] text-[13px] focus:border-[#7C3AED] outline-none"
              />
            </div>
            <div>
              <label className="block text-[11.5px] font-semibold text-[#6B6B6B] mb-1">
                Arabic Name (Optional)
              </label>
              <input
                type="text"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                placeholder="e.g., طلبات الوصول"
                className="w-full px-3 py-2 rounded-lg border border-[#E0E0E0] text-[13px] focus:border-[#7C3AED] outline-none"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white text-[12px] font-semibold hover:bg-[#6D28D9] disabled:opacity-50 transition"
              >
                Save
              </button>
              <button
                onClick={cancel}
                className="px-3 py-1.5 rounded-lg border border-[#E0E0E0] text-[#6B6B6B] text-[12px] font-semibold hover:bg-[#F9F8FF] transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories list */}
      <div className="space-y-2">
        {categories.length === 0 ? (
          <div className="text-center py-8 text-[#6B6B6B]">
            <p className="text-[13px]">No categories yet. Create one to get started.</p>
          </div>
        ) : (
          categories.map((cat: any) => (
            <div
              key={cat.id}
              className="flex items-center justify-between p-3 rounded-lg border border-[#E0E0E0] hover:border-[#D0D0D0] hover:bg-[#F9F8FF] transition"
            >
              <div>
                <div className="text-[13px] font-semibold text-[#2C2C2C]">{cat.name}</div>
                {cat.nameAr && <div className="text-[12px] text-[#6B6B6B] mt-0.5">{cat.nameAr}</div>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(cat)}
                  className="px-2.5 py-1 rounded text-[12px] font-semibold text-[#7C3AED] hover:bg-[#EDE9FE] transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm("Delete this category?")) {
                      deleteMutation.mutate({ id: cat.id });
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="p-1.5 rounded text-[#DC2626] hover:bg-[#FEE2E2] disabled:opacity-50 transition"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
